import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, Subscription, fromEvent, merge, timer } from 'rxjs';

@Injectable()
export class InactivityService implements OnDestroy {
    private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    private readonly warningSubject = new Subject<void>();
    private readonly warningClearedSubject = new Subject<void>();
    private isEnabled = false;

    warning$ = this.warningSubject.asObservable();
    warningCleared$ = this.warningClearedSubject.asObservable();

    private activitySubscription: Subscription = new Subscription();
    private inactivityTimerSubscription: Subscription = new Subscription();
    private isWarningVisible = false;
    private inactivityTimeoutSeconds = 0;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private ngZone: NgZone
    ) {
    }

    start(inactivityTimeoutSeconds: number, countdownSeconds = 10): void {
        this.stop();

        this.inactivityTimeoutSeconds = inactivityTimeoutSeconds;
        this.isEnabled = true;

        this.ngZone.runOutsideAngular(() => {
            this.activitySubscription = merge(
                ...this.activityEvents.map(eventName => fromEvent(this.document, eventName))
            ).subscribe(() => {
                // Stay outside Angular zone — activity only reschedules a timer;
                // there is no UI update needed here, so no CD should be triggered.
                this.onActivity();
            });
        });

        this.scheduleInactivityTimer();
    }

    stop(): void {
        this.isEnabled = false;
        this.isWarningVisible = false;
        this.activitySubscription.unsubscribe();
        this.inactivityTimerSubscription.unsubscribe();
    }

    continueSession(): void {
        this.isWarningVisible = false;
        this.warningClearedSubject.next();
        this.scheduleInactivityTimer();
    }

    ngOnDestroy(): void {
        this.stop();
    }

    private onActivity(): void {
        if (!this.isEnabled) {
            return;
        }

        if (this.isWarningVisible) {
            return;
        }

        this.scheduleInactivityTimer();
    }

    private scheduleInactivityTimer(): void {
        this.inactivityTimerSubscription.unsubscribe();

        if (!this.isEnabled) {
            return;
        }

        // Run the timer outside Angular's zone so it does not keep the zone
        // unstable and does not trigger change detection while simply waiting.
        // Only re-enter the zone when the warning must actually be displayed.
        this.ngZone.runOutsideAngular(() => {
            this.inactivityTimerSubscription = timer(this.inactivityTimeoutSeconds * 1000).subscribe(() => {
                this.ngZone.run(() => this.openWarning());
            });
        });
    }

    private openWarning(): void {
        if (!this.isEnabled) {
            return;
        }

        this.isWarningVisible = true;
        this.warningSubject.next();
    }
}
